/**
 * Text matching for chat search results — the client half of D6.
 *
 * `tokenizeQuery` is the SAME tokenizer as the backend's
 * `BackendPheonix.Chat.TextMatch.terms/1`, so the highlight marks exactly what
 * the server matched. Both test suites carry the same vector table
 * (tests/chatSearchHighlight.test.js ↔ test/chat_text_match_test.exs); a
 * change here without the Elixir side (or the other way round) is drift.
 *
 * Rule: trim; > 200 chars → query_too_long. Terms come from
 * /"([^"]*)"|(\S+)/ — a closed "quoted phrase" is one term (trimmed), anything
 * else splits on whitespace, so an unterminated quote is literal text. Each
 * term is lower-cased and clipped to 100 chars; empties are dropped; at most
 * the first 10 terms count. A message matches when EVERY term is a
 * case-insensitive substring of its haystack.
 */

export const MAX_QUERY_CHARS = 200;
export const MAX_TERMS = 10;
export const MAX_TERM_CHARS = 100;

export function tokenizeQuery(q) {
  const s = String(q ?? '').trim();
  if (s.length > MAX_QUERY_CHARS) return { error: 'query_too_long' };
  const terms = [];
  const re = /"([^"]*)"|(\S+)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const raw = (m[1] !== undefined ? m[1] : m[2]).trim().toLowerCase().slice(0, MAX_TERM_CHARS);
    if (raw) terms.push(raw);
  }
  return { terms: terms.slice(0, MAX_TERMS) };
}

/** Terms for highlighting; an over-long query highlights nothing. */
export function searchTerms(q) {
  return tokenizeQuery(q).terms || [];
}

/**
 * Split `text` into [{ text, hit }] with every case-insensitive occurrence of
 * any term marked; overlapping and adjacent hits merge into one.
 */
export function highlightSegments(text, terms) {
  const s = String(text ?? '');
  if (!s) return [];
  const lower = s.toLowerCase();
  // toLowerCase can change the length of a few characters (İ); indices from
  // the lowered copy are only safe when it did not.
  const sameLength = lower.length === s.length;
  const ranges = [];
  if (sameLength) {
    for (const term of terms || []) {
      if (!term) continue;
      let from = 0;
      let at;
      while ((at = lower.indexOf(term, from)) !== -1) {
        ranges.push([at, at + term.length]);
        from = at + 1;
      }
    }
  }
  if (!ranges.length) return [{ text: s, hit: false }];
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [ranges[0].slice()];
  for (const [a, b] of ranges.slice(1)) {
    const last = merged[merged.length - 1];
    if (a <= last[1]) last[1] = Math.max(last[1], b);
    else merged.push([a, b]);
  }
  const out = [];
  let pos = 0;
  for (const [a, b] of merged) {
    if (a > pos) out.push({ text: s.slice(pos, a), hit: false });
    out.push({ text: s.slice(a, b), hit: true });
    pos = b;
  }
  if (pos < s.length) out.push({ text: s.slice(pos), hit: false });
  return out;
}

/**
 * The searchable metadata string a result card shows (and highlights) next to
 * the body — the D7 list: attachment name, share label / title / workspace,
 * legacy ref label, and a vault secret's LABEL. Never ct, iv, keys, token or
 * url, and never the reply quote (it belongs to another message).
 */
export function resultHaystackField(m) {
  const md = m?.metadata || {};
  const str = (v) => (typeof v === 'string' ? v : '');
  switch (m?.kind) {
    case 'image':
    case 'file':
      return str(md.name);
    case 'share':
      return str(md.label) || str(md.title) || str(md.workspace_name);
    case 'ref':
      return str(md.label);
    case 'secret':
      return str(md.label);
    default:
      return '';
  }
}
